import { ApiError, errorMessage, normalizeApiError } from './apiError';
import axiosInstance from './axiosInstance';
import type { SkillSurface, WebsiteSkillSummary } from './websiteSkillTypes';

export type { SkillSurface, WebsiteSkillPage, WebsiteSkillStep, WebsiteSkillSummary } from './websiteSkillTypes';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export type SkillStatus = 'generating' | 'active' | 'publish_failed' | 'archived';
export type SkillInteractionType = 'ui' | 'api';
export type SkillRuntimeType = 'instructions' | 'hosted' | 'website';
export type SkillFundingMode = 'author_funded' | 'system_funded';
export type SkillDeploymentStatus = 'not_deployed' | 'queued' | 'building' | 'deploying' | 'ready'
  | 'failed' | 'disable_queued' | 'disabling' | 'disabled';
export type SkillEndpointStatus = 'pending' | 'healthy' | 'unhealthy' | 'disabled';
export type SkillStackStateMode = 'none' | 'memory' | 'persistent';
export type CashoutStatus = 'in_review' | 'in_progress' | 'paid';
export type CashoutCurrency = 'USD' | 'INR';
export type SkillConfigurationScope = 'owner' | 'user';
export type SkillConfigValue = string | number | boolean | Record<string, unknown> | unknown[] | null;
export type SkillConfigValueType = 'string' | 'number' | 'boolean' | 'json';
type SkillIconBlob = Parameters<typeof window.URL.createObjectURL>[0];

export const SKILL_OPERATION_MAX_CREDITS = 10000;
export const SKILL_DEVELOPMENT_SUPPORT_MAX_CREDITS = 100000;

export interface SkillConfigField {
  key: string;
  label: string;
  description?: string | null;
  value_type: SkillConfigValueType;
  scope: SkillConfigurationScope;
  is_secret: boolean;
  required: boolean;
  default_value?: SkillConfigValue;
  env_var?: string | null;
  sort_order?: number;
}

export interface SkillConfigurationField extends SkillConfigField {
  id: string;
  configured: boolean;
  has_default?: boolean;
}

export interface SkillConfiguration {
  skill_id: string;
  fields: SkillConfigurationField[];
}

export interface SaveSkillConfigurationPayload {
  config_values: Record<string, SkillConfigValue>;
  secret_values: Record<string, string>;
  scopes?: Record<string, SkillConfigurationScope>;
  unset_keys?: string[];
  save: true;
}

export interface SkillOperation {
  id: string;
  slug: string;
  name: string;
  description: string;
  credit_cost: number;
  entrypoint: string;
  api_enabled?: boolean;
  api_slug?: string | null;
  api_id?: string | null;
  api_url?: string | null;
}

export interface SkillDevelopmentCreditTotals {
  initial: number;
  continuous: number;
  supported: number;
  spent: number;
  available: number;
}

export interface LegacySkillDevelopmentCredits {
  initial?: number;
  continuous_available?: number;
  continuous_spent?: number;
  continuous_total?: number;
  continuous_allocated?: number;
  supported?: number;
}

export const normalizeSkillDevelopmentTotals = (
  totals?: Partial<SkillDevelopmentCreditTotals> | null,
  legacy?: LegacySkillDevelopmentCredits | null,
): SkillDevelopmentCreditTotals => ({
  initial: totals?.initial ?? legacy?.initial ?? 0,
  continuous: totals?.continuous ?? legacy?.continuous_allocated ?? legacy?.continuous_total ?? 0,
  supported: totals?.supported ?? legacy?.supported ?? 0,
  spent: totals?.spent ?? legacy?.continuous_spent ?? 0,
  available: totals?.available ?? legacy?.continuous_available ?? 0,
});

export interface SkillFeedbackSummary {
  skill_id: string;
  success_rate: number | null;
  total_feedback: number;
  successful_invocations: number;
  problem_reports: number;
}

export type SkillProblemStatus = 'open' | 'planned' | 'in_progress' | 'resolved';

export interface SkillProblem {
  id: string;
  title: string;
  description: string;
  status: SkillProblemStatus;
  frequency: number;
  rice_score: number;
  estimated_credits: number;
  credits_committed: number;
  created_at: string;
  updated_at?: string | null;
}

export type SkillDevelopmentEntryKind = 'initial' | 'continuous' | 'support' | 'refund'
  | 'operation_revenue' | 'manual_support' | 'backlog_spend' | 'backlog_refund';

export interface SkillDevelopmentEntry {
  id: string;
  kind: SkillDevelopmentEntryKind;
  credits: number;
  description: string;
  created_at: string;
}

export interface SkillDevelopment {
  skill_id: string;
  totals: SkillDevelopmentCreditTotals;
  history: SkillDevelopmentEntry[];
}

export interface SubmitSkillFeedbackPayload {
  met_expectations: boolean;
  problem?: string;
  source?: 'agent' | 'user';
}

export interface SkillInvocationFeedback {
  id: string;
  usage_id: string;
  skill_id: string;
  met_expectations: boolean;
  problem: string | null;
  created_at: string;
}

export interface SupportSkillPayload {
  credits: number;
  idempotency_key?: string;
}

export interface SkillSupport {
  id?: string;
  transaction_id?: string;
  skill_id?: string;
  credits: number;
  development_credits?: Record<string, number>;
  created_at?: string;
}

export interface StoreSkill {
  execution_timeout_seconds?: number;
  id: string;
  name: string;
  display_name: string;
  description: string;
  usage_doc: string;
  github_repo_url: string | null;
  icon_url: string;
  status: SkillStatus;
  is_public: boolean;
  is_system: boolean;
  interaction_type: SkillInteractionType;
  accepts_free_text: boolean;
  runtime_type: SkillRuntimeType;
  funding_mode?: SkillFundingMode;
  deployment?: SkillDeploymentSummary | null;
  owner: string | null;
  owner_user_id: string;
  usage_count: number;
  relevance_score: number | null;
  success_rate?: number | null;
  feedback_count?: number;
  open_problem_count?: number;
  development_credit_totals?: Partial<SkillDevelopmentCreditTotals> | null;
  development_credits?: LegacySkillDevelopmentCredits | null;
  config_fields?: SkillConfigField[];
  operations: SkillOperation[];
  surfaces?: SkillSurface[];
  website?: WebsiteSkillSummary | null;
  created_at: string;
}

export interface SkillDeploymentSummary {
  status: SkillDeploymentStatus;
  endpoint_status: SkillEndpointStatus;
  available: boolean;
  blocker?: string | null;
}

export interface SkillDeployment extends SkillDeploymentSummary {
  skill_id: string;
  runtime_type: 'hosted';
  desired_state: 'ready' | 'disabled';
  api_url: string | null;
  endpoint_checked_at: string | null;
  updated_at: string;
  deployment_id?: string | null;
  revision?: number;
  manifest_sha256?: string;
  container_port?: number;
  health_path?: string;
  invoke_path?: string;
  request_timeout_seconds?: number;
  image_reference?: string | null;
  last_error?: string | null;
  deployed_at?: string | null;
  request?: {
    id: string;
    action: 'deploy' | 'disable';
    status: 'pending' | 'processing' | 'completed' | 'failed';
    attempts: number;
    created_at: string;
  } | null;
}

export interface SkillStackComponent {
  name: string;
  kind: 'http' | 'worker';
  port: number | null;
  replicas: number;
  command: string[] | null;
  mount_state: boolean;
}

export interface SkillStackState {
  mode: SkillStackStateMode;
  size: string;
  mount_path: string;
  storage_class_name: string | null;
}

export interface SkillStack {
  skill_id: string;
  namespace: string;
  version: number;
  components: SkillStackComponent[];
  state: SkillStackState;
}

export interface UpdateSkillStackPayload {
  components: SkillStackComponent[];
  state: SkillStackState;
}

export interface DeploySkillPayload {
  container_port?: number;
  health_path?: '/health';
  invoke_path?: '/v1/operations';
  request_timeout_seconds?: number;
}

export interface PublishSkillResponse {
  skill: StoreSkill;
  deployment: SkillDeployment | null;
  deployment_warning: string | null;
}

export interface SkillStoreResponse {
  items: StoreSkill[];
  page: number;
  page_size: number;
  total: number;
}

export type LandingSkillResponse = StoreSkill[] | { items: StoreSkill[] };

export interface GenerateSkillPayload {
  description: string;
  is_public: boolean;
  interaction_type: SkillInteractionType;
  accepts_free_text: boolean;
  runtime_type: SkillRuntimeType;
  funding_mode: SkillFundingMode;
}

export interface CreateBlankSkillPayload {
  name: string;
  display_name: string;
  description: string;
  usage_doc?: string;
  is_public: boolean;
  interaction_type: SkillInteractionType;
  accepts_free_text: boolean;
  runtime_type: SkillRuntimeType;
  config_fields: SkillConfigField[];
}

export interface GeneratedSkill extends StoreSkill {
  reused_existing: boolean;
}

export interface SkillInvocation {
  usage_id: string;
  skill_id: string;
  operation_id: string;
  credit_cost: number;
  admin_share: number;
  owner_share: number;
  execution: {
    status: 'completed' | 'incomplete';
    message?: string;
    output: unknown;
    replayed: boolean;
  };
}

export interface SkillInvocationState extends Omit<SkillInvocation, 'execution'> {
  retry_after_seconds?: number;
  execution: Omit<SkillInvocation['execution'], 'status'> & {
    status: 'pending' | 'completed' | 'incomplete' | 'failed';
  };
}

export const SKILL_INVOCATION_TIMEOUT_MS = 150000;

export interface InvokeSkillOperationPayload {
  project_id?: string;
  context?: string;
  idempotency_key?: string;
  config_values?: Record<string, SkillConfigValue>;
  secret_values?: Record<string, string>;
  save_config?: boolean;
}

export interface UpdateSkillOperationApiPayload {
  api_enabled: boolean;
  api_slug?: string | null;
}

export interface CreateSkillOperationPayload {
  slug: string;
  name: string;
  description: string;
  credit_cost: number;
  entrypoint: string;
  api_enabled: boolean;
  api_slug?: string | null;
}

export type UpdateSkillOperationPayload = Partial<CreateSkillOperationPayload>;

export interface UpdateSkillVisibilityPayload {
  is_public: boolean;
}

export interface PublishedSkillApiOperation {
  operation_id: string;
  name: string;
  description: string;
  api_slug: string;
  method: string;
  endpoint_url: string;
  credit_cost: number;
  idempotency_required?: boolean;
  curl_example?: string | null;
}

export interface PublishedSkillApi {
  skill_id: string;
  name: string;
  display_name?: string;
  description?: string | null;
  authentication?: {
    required: boolean;
    credential_scope?: 'account';
    warning?: string;
    schemes: Array<Record<string, string>>;
  };
  secret_delivery?: {
    mode: 'presence_only';
    description: string;
  };
  config_fields?: SkillConfigurationField[];
  operations: PublishedSkillApiOperation[];
}

export interface EarningsWallet {
  skill_earned_credits: number;
  clone_earned_credits: number;
  available_credits: number;
  currency: CashoutCurrency;
  pro_credit_rate: number;
  cash_value: number;
}

export interface CashoutBankDetails {
  account_name: string;
  account_number: string;
  bank_name: string;
  routing_code?: string;
  branch?: string;
}

export interface CreateCashoutPayload {
  credits: number;
  currency: CashoutCurrency;
  bank_details: CashoutBankDetails;
}

export interface CashoutRequest {
  id: string;
  user_id: string;
  user_email: string | null;
  credits: number;
  payout_amount: number;
  payout_currency: CashoutCurrency;
  pro_credit_rate: number;
  earning_sources: Record<string, string>;
  bank_account_last4: string;
  status: CashoutStatus;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
}

export interface AdminCashoutRequest extends CashoutRequest {
  bank_details: CashoutBankDetails | null;
  bank_details_error: string | null;
  admin_comment: string | null;
}

export interface UpdateCashoutPayload {
  status: CashoutStatus;
  admin_comment?: string | null;
}

interface SkillStoreErrorResponse {
  status?: number;
  data?: unknown;
}

const getSkillStoreErrorResponse = (error: unknown): SkillStoreErrorResponse | null => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return null;
  const response = error.response;

  return typeof response === 'object' && response !== null ? response as SkillStoreErrorResponse : null;
};

export const getSkillStoreError = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) return error.message.toLowerCase() === 'not found'
    ? fallback : errorMessage(error, fallback);
  const data = getSkillStoreErrorResponse(error)?.data;
  const detail = typeof data === 'object' && data !== null && 'detail' in data && typeof data.detail === 'string'
    ? data.detail.trim()
    : '';

  // FastAPI's generic 404 text is an implementation detail, not useful UI.
  // Keep specific errors while replacing repeated raw "Not Found" messages
  // with a contextual message supplied by the caller.
  return detail && detail.toLowerCase() !== 'not found' ? detail : fallback;
};

export const isSkillInvocationFingerprintMismatch = (error: unknown): boolean => (
  (error instanceof ApiError ? error.status : getSkillStoreErrorResponse(error)?.status) === 400
  && getSkillStoreError(error, '') === 'Idempotency key does not match this operation request'
);

export const shouldPreserveSkillInvocationRetry = (error: unknown): boolean => {
  const normalized = normalizeApiError(error);

  if (normalized.code === 'SKILL_EXECUTION_FAILED') return false;
  const status = error instanceof ApiError ? error.status : getSkillStoreErrorResponse(error)?.status;

  return status === undefined || status === 409 || status >= 500;
};

export const resolveSkillIconUrl = (iconUrl: string): string => {
  if (/^(https?:|data:|blob:)/i.test(iconUrl)) return iconUrl;

  return iconUrl.startsWith('/') ? `${API_BASE_URL}${iconUrl}` : iconUrl;
};

export const resolveSkillApiUrl = (endpoint: string): string => {
  if (/^https?:/i.test(endpoint)) return endpoint;
  const baseUrl = API_BASE_URL
    ? new URL(API_BASE_URL, window.location.origin).toString().replace(/\/$/, '')
    : window.location.origin;

  return endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
};

export const skillStoreService = {
  async listLandingSkills(query = ''): Promise<StoreSkill[]> {
    const cleanQuery = query.trim();
    const response = await axiosInstance.get<LandingSkillResponse>('/api/v1/skills/landing-selector', {
      params: { limit: 50, ...(cleanQuery ? { q: cleanQuery } : {}) },
    });

    return Array.isArray(response.data) ? response.data : response.data.items;
  },

  async listSkills(query = '', page = 1, pageSize = 12, surface?: SkillSurface): Promise<SkillStoreResponse> {
    const response = await axiosInstance.get<SkillStoreResponse>('/api/v1/skills', {
      params: { q: query, page, page_size: pageSize, ...(surface ? { surface } : {}) },
    });

    return response.data;
  },

  async listMySkills(query = '', page = 1, pageSize = 12): Promise<SkillStoreResponse> {
    const response = await axiosInstance.get<SkillStoreResponse>('/api/v1/skills/mine', {
      params: { q: query, page, page_size: pageSize },
    });

    return response.data;
  },

  async getSkill(skillId: string): Promise<StoreSkill> {
    const response = await axiosInstance.get<StoreSkill>(`/api/v1/skills/${skillId}`);

    return response.data;
  },

  async getDeployment(skillId: string): Promise<SkillDeployment> {
    const response = await axiosInstance.get<SkillDeployment>(`/api/v1/skills/${skillId}/deployment`);

    return response.data;
  },

  async getSkillStack(skillId: string): Promise<SkillStack> {
    const response = await axiosInstance.get<SkillStack>(`/api/v1/skills/${skillId}/stack`);

    return response.data;
  },

  async updateSkillStack(skillId: string, payload: UpdateSkillStackPayload): Promise<SkillStack> {
    const response = await axiosInstance.put<SkillStack>(`/api/v1/skills/${skillId}/stack`, payload);

    return response.data;
  },

  async deploySkill(skillId: string, payload: DeploySkillPayload = {}): Promise<SkillDeployment> {
    const response = await axiosInstance.post<SkillDeployment>(
      `/api/v1/skills/${skillId}/deployment`,
      payload,
    );

    return response.data;
  },

  async disableSkillDeployment(skillId: string): Promise<SkillDeployment> {
    const response = await axiosInstance.delete<SkillDeployment>(`/api/v1/skills/${skillId}/deployment`);

    return response.data;
  },

  async publishSkill(skillId: string): Promise<PublishSkillResponse> {
    const response = await axiosInstance.post<PublishSkillResponse>(`/api/v1/skills/${skillId}/publish`);

    return response.data;
  },

  async getOwnedSkillIcon(skillId: string): Promise<SkillIconBlob> {
    const response = await axiosInstance.get<SkillIconBlob>(`/api/v1/skills/${skillId}/icon.svg`, {
      responseType: 'blob',
    });

    return response.data;
  },

  async updateVisibility(
    skillId: string,
    payload: UpdateSkillVisibilityPayload,
  ): Promise<StoreSkill> {
    const response = await axiosInstance.patch<StoreSkill>(
      `/api/v1/skills/${skillId}/visibility`,
      payload,
    );

    return response.data;
  },

  async generateSkill(payload: GenerateSkillPayload): Promise<GeneratedSkill> {
    const response = await axiosInstance.post<GeneratedSkill>('/api/v1/skills/generate', payload, {
      timeout: 120000,
    });

    return response.data;
  },

  async createBlankSkill(payload: CreateBlankSkillPayload): Promise<GeneratedSkill> {
    const response = await axiosInstance.post<GeneratedSkill>('/api/v1/skills/blank', payload);

    return response.data;
  },

  async getConfiguration(skillId: string): Promise<SkillConfiguration> {
    const response = await axiosInstance.get<
      SkillConfiguration | SkillConfigurationField[] | { config_fields: SkillConfigurationField[] }
    >(
      `/api/v1/skills/${skillId}/config`,
    );

    if (Array.isArray(response.data)) return { skill_id: skillId, fields: response.data };
    if ('fields' in response.data) return response.data;

    return { skill_id: skillId, fields: response.data.config_fields };
  },

  async saveConfiguration(
    skillId: string,
    payload: SaveSkillConfigurationPayload,
  ): Promise<SkillConfiguration> {
    const response = await axiosInstance.put<
      SkillConfiguration | SkillConfigurationField[] | { config_fields: SkillConfigurationField[] }
    >(
      `/api/v1/skills/${skillId}/config`,
      payload,
    );

    if (Array.isArray(response.data)) return { skill_id: skillId, fields: response.data };
    if ('fields' in response.data) return response.data;

    return { skill_id: skillId, fields: response.data.config_fields };
  },

  async invokeOperation(
    skillId: string,
    operationId: string,
    payload: InvokeSkillOperationPayload,
    options: { timeout?: number; signal?: globalThis.AbortSignal } = {},
  ): Promise<SkillInvocation> {
    const response = await axiosInstance.post<SkillInvocation>(
      `/api/v1/skills/${skillId}/operations/${operationId}/invoke`,
      { ...payload, idempotency_key: payload.idempotency_key || window.crypto.randomUUID() },
      { timeout: SKILL_INVOCATION_TIMEOUT_MS, ...options },
    );

    return response.data;
  },

  async getInvocationStatus(
    skillId: string,
    operationId: string,
    requestId: string,
    options: { timeout?: number; signal?: globalThis.AbortSignal } = {},
  ): Promise<SkillInvocationState> {
    // This loop owns retries and its deadline; do not stack global GET retries.
    const config = { ...options, skipSafeRetry: true };
    const response = await axiosInstance.get<SkillInvocationState>(
      `/api/v1/skills/${skillId}/operations/${operationId}/invocations/${encodeURIComponent(requestId)}`,
      config,
    );

    return response.data;
  },

  async submitInvocationFeedback(
    skillId: string,
    usageId: string,
    payload: SubmitSkillFeedbackPayload,
  ): Promise<SkillInvocationFeedback> {
    const response = await axiosInstance.post<SkillInvocationFeedback>(
      `/api/v1/skills/${skillId}/usages/${usageId}/feedback`,
      payload,
    );

    return response.data;
  },

  async getFeedbackSummary(skillId: string): Promise<SkillFeedbackSummary> {
    const response = await axiosInstance.get<SkillFeedbackSummary>(
      `/api/v1/skills/${skillId}/feedback/summary`,
    );

    return response.data;
  },

  async listProblems(skillId: string): Promise<SkillProblem[]> {
    const response = await axiosInstance.get<SkillProblem[] | { items: SkillProblem[] }>(
      `/api/v1/skills/${skillId}/problems`,
    );

    return Array.isArray(response.data) ? response.data : response.data.items;
  },

  async getDevelopment(skillId: string): Promise<SkillDevelopment> {
    const response = await axiosInstance.get<Partial<SkillDevelopment> & {
      development_credit_totals?: Partial<SkillDevelopmentCreditTotals>;
      development_credits?: LegacySkillDevelopmentCredits;
      entries?: Array<{
        id: string;
        type: SkillDevelopmentEntryKind;
        credits: number;
        description: string;
        created_at: string;
      }>;
    }>(
      `/api/v1/skills/${skillId}/development`,
    );
    const data = response.data;

    return {
      skill_id: data.skill_id || skillId,
      totals: normalizeSkillDevelopmentTotals(
        data.totals || data.development_credit_totals,
        data.development_credits,
      ),
      history: Array.isArray(data.history)
        ? data.history
        : (data.entries || []).map((entry) => ({ ...entry, kind: entry.type })),
    };
  },

  async supportSkill(skillId: string, payload: SupportSkillPayload): Promise<SkillSupport> {
    const response = await axiosInstance.post<SkillSupport>(
      `/api/v1/skills/${skillId}/support`,
      { ...payload, idempotency_key: payload.idempotency_key || window.crypto.randomUUID() },
    );

    return response.data;
  },

  async updateOperationApi(
    skillId: string,
    operationId: string,
    payload: UpdateSkillOperationApiPayload,
  ): Promise<SkillOperation> {
    const response = await axiosInstance.patch<SkillOperation>(
      `/api/v1/skills/${skillId}/operations/${operationId}/api`,
      payload,
    );

    return response.data;
  },

  async createOperation(
    skillId: string,
    payload: CreateSkillOperationPayload,
  ): Promise<SkillOperation> {
    const response = await axiosInstance.post<SkillOperation>(
      `/api/v1/skills/${skillId}/operations`,
      payload,
    );

    return response.data;
  },

  async updateOperation(
    skillId: string,
    operationId: string,
    payload: UpdateSkillOperationPayload,
  ): Promise<SkillOperation> {
    const response = await axiosInstance.patch<SkillOperation>(
      `/api/v1/skills/${skillId}/operations/${operationId}`,
      payload,
    );

    return response.data;
  },

  async deleteOperation(skillId: string, operationId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/skills/${skillId}/operations/${operationId}`);
  },

  async getPublishedApi(skillId: string): Promise<PublishedSkillApi> {
    const response = await axiosInstance.get<PublishedSkillApi>(`/api/v1/skill-api/${skillId}`);

    return response.data;
  },

  async getEarningsWallet(currency?: CashoutCurrency): Promise<EarningsWallet> {
    const response = await axiosInstance.get<EarningsWallet>('/api/v1/skills/earnings/wallet', {
      params: currency ? { currency } : undefined,
    });

    return response.data;
  },

  async listCashouts(): Promise<CashoutRequest[]> {
    const response = await axiosInstance.get<CashoutRequest[]>('/api/v1/skills/earnings/cashouts');

    return response.data;
  },

  async createCashout(payload: CreateCashoutPayload): Promise<CashoutRequest> {
    const response = await axiosInstance.post<CashoutRequest>('/api/v1/skills/earnings/cashouts', payload);

    return response.data;
  },

  async listAdminCashouts(status?: CashoutStatus): Promise<AdminCashoutRequest[]> {
    const response = await axiosInstance.get<AdminCashoutRequest[]>('/api/v1/admin/cashouts', {
      params: status ? { status } : undefined,
    });

    return response.data;
  },

  async updateAdminCashout(requestId: string, payload: UpdateCashoutPayload): Promise<AdminCashoutRequest> {
    const response = await axiosInstance.patch<AdminCashoutRequest>(
      `/api/v1/admin/cashouts/${requestId}`,
      payload,
    );

    return response.data;
  },
};
